import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { Link } from 'react-router-dom';

const StructuralValvularHeartDiseaseFH = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fef3f2] via-white to-[#fde8e7]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full mb-6">
              <i className="ri-heart-line text-purple-600"></i>
              <span className="text-sm font-medium text-purple-700">Heart Disease</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-light text-gray-900 mb-6 leading-tight">
              Structural &amp; Valvular Heart Disease + <span className="font-semibold text-[#f04e58]">Familial Hypercholesterolemia (FH)</span>
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed font-light max-w-3xl mx-auto">
              An Overlooked Risk in Familial Hypercholesterolemia
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="pb-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              When most people hear about <strong>Familial Hypercholesterolemia (FH)</strong>, they think about heart attacks and blocked coronary arteries.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              But FH does more than narrow arteries.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Over time, chronically elevated LDL cholesterol can affect the <strong>structure of the heart itself</strong> — including the valves, the aortic root, and the heart muscle. In severe cases, especially <strong>Homozygous Familial Hypercholesterolemia (HoFH)</strong>, structural heart disease can begin early in life.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Understanding this connection is critical for early detection and prevention.
            </p>
          </div>
        </div>
      </section>

      {/* How High LDL Impacts Heart Structure */}
      <section className="pb-12">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            How High LDL Impacts <span className="font-semibold text-[#f04e58]">Heart Structure</span>
          </h2>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100">
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              LDL cholesterol doesn't only accumulate inside coronary arteries. It can also deposit in:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-purple-600 text-xl mt-0.5"></i>
                <span className="text-gray-700">The aortic root</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-purple-600 text-xl mt-0.5"></i>
                <span className="text-gray-700">Heart valve leaflets</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-purple-600 text-xl mt-0.5"></i>
                <span className="text-gray-700">Large arteries near the heart</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-purple-600 text-xl mt-0.5"></i>
                <span className="text-gray-700">The heart muscle (indirectly through ischemia)</span>
              </li>
            </ul>
            <p className="text-base text-gray-700 leading-relaxed mt-6">
              Over decades, this exposure contributes to <strong>thickening, calcification, and stiffness</strong> — altering how the heart functions.
            </p>
          </div>
        </div>
      </section>

      {/* Key Complications */}
      <section className="pb-12">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-light text-gray-900 mb-3 text-center">
            Key Structural &amp; Valvular Complications <span className="font-semibold text-[#f04e58]">Linked to FH</span>
          </h2>
          <p className="text-center text-gray-600 mb-10 font-light">Understanding how FH affects heart structure beyond coronary arteries</p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* 1. Aortic Root Thickening */}
            <div className="bg-white rounded-2xl border-2 border-purple-100 p-6 hover:border-purple-300 hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-purple-50 flex-shrink-0">
                  <i className="ri-heart-pulse-line text-2xl text-purple-600"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-purple-600 mb-1">1️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Aortic Root Thickening</h3>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                The aortic root is the section of the aorta that connects directly to the heart.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                In long-standing FH:
              </p>
              <ul className="space-y-2 mb-3">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-purple-600 mt-0.5"></i>
                  <span>Cholesterol deposition can occur in the aortic wall</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-purple-600 mt-0.5"></i>
                  <span>The vessel may become thickened and stiff</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-purple-600 mt-0.5"></i>
                  <span>Blood flow dynamics may be altered</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600 italic">
                This is particularly seen in severe LDL elevations, especially in HoFH.
              </p>
            </div>

            {/* 2. Aortic Valve Calcification */}
            <div className="bg-white rounded-2xl border-2 border-pink-100 p-6 hover:border-pink-300 hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-pink-50 flex-shrink-0">
                  <i className="ri-heart-add-line text-2xl text-pink-600"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-pink-600 mb-1">2️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Aortic Valve Calcification</h3>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                The aortic valve controls blood flow from the heart into the aorta.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                High LDL levels promote:
              </p>
              <ul className="space-y-2 mb-3">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-pink-600 mt-0.5"></i>
                  <span>Lipid infiltration of valve leaflets</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-pink-600 mt-0.5"></i>
                  <span>Chronic inflammation</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-pink-600 mt-0.5"></i>
                  <span>Progressive calcification</span>
                </li>
              </ul>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Over time, the valve becomes stiff and narrowed — a condition known as <strong>aortic stenosis</strong>.
              </p>
              <p className="text-sm text-gray-600 italic">
                Research shows that elevated LDL and Lipoprotein(a) levels — both common in FH — accelerate aortic valve calcification.
              </p>
            </div>

            {/* 3. Supravalvular Aortic Stenosis */}
            <div className="bg-white rounded-2xl border-2 border-red-100 p-6 hover:border-red-300 hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-red-50 flex-shrink-0">
                  <i className="ri-alert-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-red-600 mb-1">3️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Supravalvular Aortic Stenosis</h3>
                  <p className="text-xs text-red-600 font-medium mt-1">(Common in HoFH)</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                In severe HoFH:
              </p>
              <ul className="space-y-2 mb-3">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-red-600 mt-0.5"></i>
                  <span>Cholesterol deposits may occur above the aortic valve</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-red-600 mt-0.5"></i>
                  <span>Narrowing develops in the ascending aorta</span>
                </li>
              </ul>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                This is called <strong>supravalvular aortic stenosis</strong> and can appear in childhood or adolescence.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                It may require:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-red-600 mt-0.5"></i>
                  <span>Early surgical intervention</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-red-600 mt-0.5"></i>
                  <span>Lifelong cardiovascular monitoring</span>
                </li>
              </ul>
              <div className="mt-4 p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-red-800 font-medium">
                  This complication highlights how aggressive LDL exposure from birth can alter large vessel anatomy.
                </p>
              </div>
            </div>

            {/* 4. Early Valvular Heart Disease */}
            <div className="bg-white rounded-2xl border-2 border-orange-100 p-6 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-orange-50 flex-shrink-0">
                  <i className="ri-heart-2-line text-2xl text-orange-600"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-orange-600 mb-1">4️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Early Valvular Heart Disease</h3>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Beyond the aortic valve, chronic lipid exposure may contribute to:
              </p>
              <ul className="space-y-2 mb-3">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-orange-600 mt-0.5"></i>
                  <span>Mitral valve thickening</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-orange-600 mt-0.5"></i>
                  <span>Calcification of valve rings</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-orange-600 mt-0.5"></i>
                  <span>Reduced valve flexibility</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600 italic">
                Although not every FH patient develops significant valve disease, the risk increases with higher LDL levels and longer exposure.
              </p>
            </div>

            {/* 5. Left Ventricular Hypertrophy */}
            <div className="bg-white rounded-2xl border-2 border-teal-100 p-6 hover:border-teal-300 hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-teal-50 flex-shrink-0">
                  <i className="ri-pulse-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-teal-600 mb-1">5️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Left Ventricular Hypertrophy (LVH)</h3>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                When arteries stiffen and valves narrow:
              </p>
              <ul className="space-y-2 mb-3">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-teal-600 mt-0.5"></i>
                  <span>The heart must pump harder</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-teal-600 mt-0.5"></i>
                  <span>Pressure load increases</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-teal-600 mt-0.5"></i>
                  <span>The left ventricle thickens (hypertrophy)</span>
                </li>
              </ul>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Left ventricular hypertrophy can develop in response to:
              </p>
              <ul className="space-y-2 mb-3">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Aortic stenosis</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Long-standing vascular stiffness</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Elevated blood pressure secondary to arterial disease</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600 italic">
                LVH increases risk of arrhythmias and heart failure.
              </p>
            </div>

            {/* 6. Ischemic Cardiomyopathy */}
            <div className="bg-white rounded-2xl border-2 border-indigo-100 p-6 hover:border-indigo-300 hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-indigo-50 flex-shrink-0">
                  <i className="ri-heart-fill text-2xl text-indigo-600"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-indigo-600 mb-1">6️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Ischemic Cardiomyopathy</h3>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                If coronary artery disease progresses (which is common in untreated FH):
              </p>
              <ul className="space-y-2 mb-3">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-indigo-600 mt-0.5"></i>
                  <span>Repeated ischemia damages heart muscle</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-indigo-600 mt-0.5"></i>
                  <span>Scar tissue forms</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-indigo-600 mt-0.5"></i>
                  <span>Pumping function declines</span>
                </li>
              </ul>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                This condition is called <strong>ischemic cardiomyopathy</strong>.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                It can lead to:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-close-circle-fill text-indigo-600 mt-0.5"></i>
                  <span>Reduced ejection fraction</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-close-circle-fill text-indigo-600 mt-0.5"></i>
                  <span>Shortness of breath</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-close-circle-fill text-indigo-600 mt-0.5"></i>
                  <span>Exercise intolerance</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-close-circle-fill text-indigo-600 mt-0.5"></i>
                  <span>Increased risk of sudden cardiac death</span>
                </li>
              </ul>
            </div>

            {/* 7. Heart Failure Secondary to CAD */}
            <div className="bg-white rounded-2xl border-2 border-rose-100 p-6 hover:border-rose-300 hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-rose-50 flex-shrink-0">
                  <i className="ri-heart-line text-2xl text-rose-600"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-rose-600 mb-1">7️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Heart Failure Secondary to Coronary Artery Disease</h3>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Heart failure occurs when the heart cannot pump effectively.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                In FH, heart failure may develop due to:
              </p>
              <ul className="space-y-2 mb-3">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-rose-600 mt-0.5"></i>
                  <span>Extensive multi-vessel coronary artery disease</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-rose-600 mt-0.5"></i>
                  <span>Recurrent heart attacks</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-rose-600 mt-0.5"></i>
                  <span>Structural valve abnormalities</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-arrow-right-s-fill text-rose-600 mt-0.5"></i>
                  <span>Chronic pressure overload</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600 italic">
                Without early LDL reduction, cumulative damage increases the likelihood of long-term cardiac dysfunction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Structural Disease Develops in FH */}
      <section className="pb-12">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            Why Structural Disease <span className="font-semibold text-[#f04e58]">Develops in FH</span>
          </h2>
          <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              The underlying driver is <strong>lifetime LDL exposure</strong>.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              In the general population, structural heart disease often appears after decades of aging.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              In FH:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-[#f04e58] text-xl mt-0.5"></i>
                <span className="text-gray-700">Elevated LDL is present from birth</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-[#f04e58] text-xl mt-0.5"></i>
                <span className="text-gray-700">Cholesterol accumulates earlier</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-[#f04e58] text-xl mt-0.5"></i>
                <span className="text-gray-700">Inflammation persists longer</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-[#f04e58] text-xl mt-0.5"></i>
                <span className="text-gray-700">Calcification processes accelerate</span>
              </li>
            </ul>
            <p className="text-lg text-gray-700 leading-relaxed font-medium">
              The longer LDL remains untreated, the higher the structural burden.
            </p>
          </div>
        </div>
      </section>

      {/* The Special Case of HoFH */}
      <section className="pb-12">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            The Special Case of <span className="font-semibold text-[#f04e58]">HoFH</span>
          </h2>
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 border-2 border-red-200">
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Individuals with <strong>Homozygous FH</strong> face the most aggressive risk:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-alert-fill text-red-600 text-xl mt-0.5"></i>
                <span className="text-gray-700">LDL levels often exceed 400–500 mg/dL</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-alert-fill text-red-600 text-xl mt-0.5"></i>
                <span className="text-gray-700">Structural abnormalities may appear in childhood</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-alert-fill text-red-600 text-xl mt-0.5"></i>
                <span className="text-gray-700">Early aortic valve disease is more common</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-alert-fill text-red-600 text-xl mt-0.5"></i>
                <span className="text-gray-700">Surgical interventions may be required earlier in life</span>
              </li>
            </ul>
            <div className="mt-6 p-4 bg-red-100 rounded-lg">
              <p className="text-base text-red-900 font-semibold">
                This makes early diagnosis and aggressive treatment essential.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Can Structural Damage Be Prevented? */}
      <section className="pb-12">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            Can Structural Damage Be <span className="font-semibold text-[#f04e58]">Prevented?</span>
          </h2>
          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border-2 border-teal-200">
            <p className="text-2xl font-semibold text-teal-900 mb-6">Yes — especially when FH is diagnosed early.</p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Aggressive LDL lowering can:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                <span className="text-gray-700">Slow valve calcification</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                <span className="text-gray-700">Reduce vascular inflammation</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                <span className="text-gray-700">Stabilize plaque</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                <span className="text-gray-700">Delay or prevent structural deterioration</span>
              </li>
            </ul>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Modern therapies include:
            </p>
            <div className="grid md:grid-cols-2 gap-3 mb-6">
              <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                <i className="ri-medicine-bottle-line text-teal-600 text-xl"></i>
                <span className="text-gray-700 font-medium">High-intensity statins</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                <i className="ri-medicine-bottle-line text-teal-600 text-xl"></i>
                <span className="text-gray-700 font-medium">Ezetimibe</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                <i className="ri-medicine-bottle-line text-teal-600 text-xl"></i>
                <span className="text-gray-700 font-medium">PCSK9 inhibitors</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                <i className="ri-medicine-bottle-line text-teal-600 text-xl"></i>
                <span className="text-gray-700 font-medium">Inclisiran</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-white rounded-lg md:col-span-2">
                <i className="ri-medicine-bottle-line text-teal-600 text-xl"></i>
                <span className="text-gray-700 font-medium">LDL apheresis (in severe cases)</span>
              </div>
            </div>
            <p className="text-lg text-teal-900 font-semibold">
              The earlier LDL is controlled, the greater the protective effect.
            </p>
          </div>
        </div>
      </section>

      {/* Why Awareness Matters */}
      <section className="pb-12">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-light text-gray-900 mb-3 text-center">
            Why <span className="font-semibold text-[#f04e58]">Awareness Matters</span>
          </h2>
          <p className="text-center text-gray-600 mb-10 font-light">Early detection can prevent irreversible structural damage</p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 text-center border border-purple-100">
              <div className="text-5xl font-bold text-purple-600 mb-2">1 in 250</div>
              <p className="text-gray-700 font-medium">people have Familial Hypercholesterolemia</p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 text-center border border-red-100">
              <div className="text-5xl font-bold text-red-600 mb-2">90%</div>
              <p className="text-gray-700 font-medium">remain undiagnosed until after a major cardiac event</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Structural and valvular changes often develop <strong>silently</strong> — detectable only through imaging such as:
            </p>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg">
                <i className="ri-heart-pulse-line text-2xl text-purple-600"></i>
                <span className="text-gray-700 font-medium">Echocardiography</span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg">
                <i className="ri-heart-pulse-line text-2xl text-purple-600"></i>
                <span className="text-gray-700 font-medium">Coronary calcium scoring</span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg">
                <i className="ri-heart-pulse-line text-2xl text-purple-600"></i>
                <span className="text-gray-700 font-medium">Vascular ultrasound</span>
              </div>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Screening individuals with:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#f04e58] text-xl mt-0.5"></i>
                <span className="text-gray-700">LDL &gt;190 mg/dL</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#f04e58] text-xl mt-0.5"></i>
                <span className="text-gray-700">Family history of early heart disease</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#f04e58] text-xl mt-0.5"></i>
                <span className="text-gray-700">Early valve calcification</span>
              </li>
            </ul>
            <p className="text-lg text-gray-700 leading-relaxed mt-6 font-medium">
              can identify FH before irreversible damage occurs.
            </p>
          </div>
        </div>
      </section>

      {/* Final Takeaway */}
      <section className="pb-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-10 shadow-xl">
            <h2 className="text-3xl font-light text-white mb-6 text-center">
              Final <span className="font-semibold">Takeaway</span>
            </h2>
            <p className="text-lg text-gray-200 leading-relaxed mb-4">
              Familial Hypercholesterolemia is not just about high cholesterol.
            </p>
            <p className="text-lg text-gray-200 leading-relaxed mb-4">
              It is about:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-[#f04e58] text-xl mt-0.5"></i>
                <span className="text-gray-200">Accelerated vascular aging</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-[#f04e58] text-xl mt-0.5"></i>
                <span className="text-gray-200">Early valve calcification</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-[#f04e58] text-xl mt-0.5"></i>
                <span className="text-gray-200">Structural remodeling of the heart</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-[#f04e58] text-xl mt-0.5"></i>
                <span className="text-gray-200">Increased risk of heart failure</span>
              </li>
            </ul>
            <div className="bg-white/10 rounded-xl p-6 mb-6">
              <p className="text-xl text-white font-semibold mb-3">The good news?</p>
              <p className="text-lg text-gray-200 leading-relaxed">
                When diagnosed early, structural damage can be delayed — and in many cases <strong className="text-white">prevented</strong>.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-white/5 rounded-lg">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#f04e58] mx-auto mb-3">
                  <span className="text-white font-bold text-lg">1</span>
                </div>
                <p className="text-gray-200 font-medium">Awareness</p>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#f04e58] mx-auto mb-3">
                  <span className="text-white font-bold text-lg">2</span>
                </div>
                <p className="text-gray-200 font-medium">Testing</p>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#f04e58] mx-auto mb-3">
                  <span className="text-white font-bold text-lg">3</span>
                </div>
                <p className="text-gray-200 font-medium">Early Treatment</p>
              </div>
            </div>
            <p className="text-lg text-gray-200 leading-relaxed text-center">
              Raising awareness of FH means protecting not only arteries, but <strong className="text-white">the very structure of the heart itself</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-10 text-center shadow-xl">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/10 mx-auto mb-6">
              <i className="ri-heart-pulse-fill text-4xl text-white"></i>
            </div>
            <h2 className="text-3xl font-light text-white mb-4">
              Early Detection <span className="font-semibold">Saves Hearts</span>
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-8 max-w-2xl mx-auto">
              Structural and valvular heart disease in FH is often preventable with early diagnosis and aggressive LDL lowering. Don't wait for symptoms to appear.
            </p>
            <Link
              to="/consultation"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#f04e58] text-white text-base font-medium rounded-full hover:bg-[#d9434c] transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-check-line text-xl"></i>
              Schedule a Consultation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StructuralValvularHeartDiseaseFH;
